import { FileOpener } from '@awesome-cordova-plugins/file-opener'
import { Capacitor } from '@capacitor/core'
import { Directory, Filesystem } from '@capacitor/filesystem'
import dayjs from 'dayjs'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { logEvent } from '../../../../analytics/utils/firebaseUtils'
import Button from '../../../../components/actions/Button'
import DateInput from '../../../../components/inputs/DateInput'
import useUser from '../../../../contexts/hooks/useUser'
import { ObjectContext } from '../../../../contexts/objectContext/ObjectContext'
import { trpc } from '../../../../modules/trpc'
import PageWrapper from '../../../../wrappers/pageWrapper/PageWrapper'

const DownloadElectricCarChargingHistory = () => {
	const { user } = useUser()

	const object = useContext(ObjectContext)

	const { t } = useTranslation('pages_electricCar', { keyPrefix: 'downloadStatistics' })

	const [startDate, setStartDate] = useState(dayjs().format('YYYY-MM-DD'))
	const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'))

	const { refetch } = trpc.eNode.statistics.getUserStatisticsOnChargingSessions.useQuery(
		{
			hashedPid: user?.hashedPid ?? '',
			options: {
				startDate: dayjs(startDate).startOf('day').format('YYYY-MM-DD:HH:mm:ss'),
				endDate: dayjs(endDate).endOf('day').format('YYYY-MM-DD:HH:mm:ss'),
				type: 'vehicle',
			},
		},
		{
			enabled: false, // Disable automatic query execution
		}
	)

	const handleDownload = async () => {
		try {
			logEvent('click-ev-download-charge-history')
			const { data } = await refetch()

			if (!data) {
				console.error('No data fetched')
				return
			}

			// eslint-disable-next-line @typescript-eslint/no-var-requires

			// Convert data to CSV format
			const csvData = data.map(stat => {
				// Make sure all values are strings to avoid Excel cell reference interpretations
				return {
					[t('beginning')]: dayjs(stat.from).format('DD.MM.YYYY HH:mm'),
					[t('end')]: dayjs(stat.to).format('DD.MM.YYYY HH:mm'),
					// Format numbers as strings with proper locale formatting
					[t('totalKwh')]: `"${stat.kwhSum.toFixed(2)}"`,
					[t('totalCostWithoutTax')]: `"${stat.costSum.toFixed(2)}"`,
					[t('totalCostWithTaxIncludingVAT')]: `"${(stat.costSum * 1.255).toFixed(2)}"`,

					[t('totalWithMarginal')]:
						`"${(stat.costSum * 1.255 + (stat.kwhSum * (object.spotMarginal ?? 0)) / 100).toFixed(2)}"`,
					[t('estimatedSavings')]: `"${(stat.estimatedSavings ?? 0).toFixed(2)}"`,
				}
			})

			// Don't add manual totals - let the system handle totals automatically

			if (csvData[0] === undefined) {
				alert(t('noData'))
				return
			}

			const csvContent = [
				// Add quotes to headers to prevent Excel interpretation issues
				Object.keys(csvData[0])
					.map(key => `"${key}"`)
					.join(','),
				...csvData.map(row => Object.values(row).join(',')),
			].join('\n')

			const timestamp = dayjs().format('YYYYMMDD_HHmmss') // Format: 20250428_153045
			// Create a blob
			const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
			const filename = `${t('csvTitle')}${startDate}_${endDate}_${timestamp}.xlsx`

			const url = URL.createObjectURL(blob)

			// eslint-disable-next-line @typescript-eslint/no-var-requires
			const cvrt = require('netas-csv2xlsx')
			// Meta-Data of the xlsx
			const metaData = {
				title: '',
				subject: '',
				creator: 'Älyenergia',
				company: 'Vihrea Älyenergia',
				lastModifiedBy: 'Älyenergia',
				created: new Date(2020, 0, 1, 11, 21),
				modified: null, // now
			}

			// download the csv and convert it to a excel file
			const xlsxBlobUrl = await cvrt.Csv2Xlsx.convertCsv(
				url,
				filename,
				metaData,
				undefined,
				undefined,
				true
			)

			// // return value is a <a> element, add it to the DOM to start the download
			// document.body.appendChild(aTag)

			// // start the download automatically
			// aTag.click()

			// document.body.removeChild(aTag)

			const convertBlobToBase64 = (blob: Blob) =>
				new Promise<string | ArrayBuffer | null>((resolve, reject) => {
					const reader = new FileReader()
					reader.onerror = reject
					reader.onload = () => {
						resolve(reader.result)
					}
					reader.readAsDataURL(blob)
				})

			if (Capacitor.isNativePlatform()) {
				try {
					// Request permissions (for Android)
					await Filesystem.requestPermissions()

					const b = await fetch(xlsxBlobUrl).then(r => r.blob())
					const base64 = await convertBlobToBase64(b)

					const saveFile = await Filesystem.writeFile({
						path: filename,
						data: base64 as string,
						directory: Directory.Documents,
					})

					await FileOpener.open(
						saveFile.uri,
						'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
					)

					alert(t('success'))
				} catch (error) {
					console.error('Error in mobile download/open process:', JSON.stringify(error))
					// alert('There was an error downloading or opening the file. Please try again.')
				}
			} else {
				const b = await fetch(xlsxBlobUrl).then(r => r.blob())
				const href = window.URL.createObjectURL(b)
				downloadFile(href, filename)
				logEvent('click-download-charge-history')
			}
		} catch (error) {
			console.error('Error in browser download process:', JSON.stringify(error))
			alert(t('error'))
		}
	}

	function downloadFile(href: string, filename: string) {
		const link = document.createElement('a')
		link.style.display = 'none'
		link.href = href
		link.download = filename
		document.body.appendChild(link)
		link.click()
		setTimeout(() => {
			URL.revokeObjectURL(link.href)
			if (link.parentNode) {
				link.parentNode.removeChild(link)
			}
		}, 0)
	}

	return (
		<PageWrapper
			page='downloadElectricCarChargingHistory'
			appBarOptions={{
				label: t('title'),
				navigationType: 'back',
			}}
		>
			<div className='text-text-dark dark:text-white'>
				<div className='description'>{t('description')}</div>
				<div className='form-container flex flex-col gap-4 mt-6 mb-4'>
					<DateInput
						label={t('beginning')}
						value={startDate}
						onChange={value => setStartDate(value)}
					/>

					<DateInput label={t('end')} value={endDate} onChange={value => setEndDate(value)} />

					<Button onClick={handleDownload}>{t('download')}</Button>
				</div>
			</div>
		</PageWrapper>
	)
}

export default DownloadElectricCarChargingHistory
